import type { KioskSettings } from "@/types/homey";

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * Baseline kiosk settings. Used as the merge target in normalizeKioskSettings
 * and as the initial value for the settings store.
 */
export const DEFAULT_KIOSK: KioskSettings = {
  idleTimeoutSec: 120,
  screensaver: "clock",
  enabled: true,
  wakeSensorDeviceId: null,
  wakeOnCamera: false,
};

// ---------------------------------------------------------------------------
// normalizeKioskSettings
// ---------------------------------------------------------------------------

/**
 * Tolerates null, undefined, partial objects, and garbage values coming from
 * the jsonb column. Always returns a fully-typed KioskSettings:
 *
 * - idleTimeoutSec: clamped to [10, 3600]; non-numeric falls back to default.
 * - screensaver: must be "clock" | "blank"; anything else → "clock".
 * - enabled / wakeOnCamera: Boolean-coerced.
 * - wakeSensorDeviceId: string or null; any other type → null.
 */
export function normalizeKioskSettings(raw: unknown): KioskSettings {
  if (raw === null || raw === undefined || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...DEFAULT_KIOSK };
  }

  const obj = raw as Record<string, unknown>;

  // idleTimeoutSec
  let idleTimeoutSec: number = DEFAULT_KIOSK.idleTimeoutSec;
  if (typeof obj.idleTimeoutSec === "number" && !Number.isNaN(obj.idleTimeoutSec)) {
    idleTimeoutSec = Math.min(3600, Math.max(10, obj.idleTimeoutSec));
  }

  // screensaver
  const screensaver: KioskSettings["screensaver"] =
    obj.screensaver === "clock" || obj.screensaver === "blank"
      ? obj.screensaver
      : DEFAULT_KIOSK.screensaver;

  // enabled
  const enabled = Boolean("enabled" in obj ? obj.enabled : DEFAULT_KIOSK.enabled);

  // wakeSensorDeviceId — must be a non-null string, else null
  const wakeSensorDeviceId: string | null =
    typeof obj.wakeSensorDeviceId === "string" ? obj.wakeSensorDeviceId : null;

  // wakeOnCamera
  const wakeOnCamera = Boolean(
    "wakeOnCamera" in obj ? obj.wakeOnCamera : DEFAULT_KIOSK.wakeOnCamera,
  );

  return { idleTimeoutSec, screensaver, enabled, wakeSensorDeviceId, wakeOnCamera };
}

// ---------------------------------------------------------------------------
// frameDiffRatio
// ---------------------------------------------------------------------------

interface FrameDiffOptions {
  /** Summed absolute per-channel delta that must be exceeded to count a pixel as "changed". Default 48. */
  pixelThreshold?: number;
}

/**
 * Compares two equal-length RGBA `Uint8ClampedArray` buffers (as produced by
 * `CanvasRenderingContext2D.getImageData`) and returns the fraction [0, 1] of
 * pixels whose summed absolute channel delta meets or exceeds `pixelThreshold`
 * (default 48).
 *
 * Returns 0 for empty or mismatched-length buffers (no false motion).
 */
export function frameDiffRatio(
  a: Uint8ClampedArray,
  b: Uint8ClampedArray,
  opts?: FrameDiffOptions,
): number {
  if (a.length !== b.length || a.length === 0) return 0;

  const threshold = opts?.pixelThreshold ?? 48;
  const pixelCount = a.length / 4;
  let changed = 0;

  for (let i = 0; i < a.length; i += 4) {
    // Cast to number to satisfy noUncheckedIndexedAccess — bounds are guaranteed
    // by the loop condition (i < a.length) and the stride of 4.
    const aR = a[i] as number;
    const aG = a[i + 1] as number;
    const aB = a[i + 2] as number;
    const aA = a[i + 3] as number;
    const bR = b[i] as number;
    const bG = b[i + 1] as number;
    const bB = b[i + 2] as number;
    const bA = b[i + 3] as number;
    const delta = Math.abs(aR - bR) + Math.abs(aG - bG) + Math.abs(aB - bB) + Math.abs(aA - bA);
    if (delta >= threshold) changed++;
  }

  return changed / pixelCount;
}

// ---------------------------------------------------------------------------
// detectMotion
// ---------------------------------------------------------------------------

/**
 * Returns true when `ratio` (from frameDiffRatio) is at or above `threshold`.
 * Default threshold is 0.02 (2% of pixels changed).
 */
export function detectMotion(ratio: number, threshold = 0.02): boolean {
  return ratio >= threshold;
}
